import type { ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/utils';
import { CountUp } from './CountUp';
import { useReveal } from './useReveal';

type TileTone = 'default' | 'success' | 'warning' | 'critical';

interface DeckStatTileProps {
  label: string;
  value: number;
  hint?: string;
  icon: ReactNode;
  tone?: TileTone;
  share?: number;
  shareLabel?: string;
  isLoading?: boolean;
  to?: string;
  linkLabel?: string;
}

const TONE = {
  default: {
    label: 'text-slate-600 dark:text-slate-300',
    value: 'text-slate-900 dark:text-white',
    cardBg: 'bg-gradient-to-b from-white to-[#f5f7ff] dark:from-slate-900 dark:to-indigo-950/20 border-indigo-100/90 dark:border-indigo-900/50 shadow-[0_4px_14px_rgba(91,82,249,0.06)]',
    iconBg: 'bg-indigo-100/80 dark:bg-indigo-950/60 text-[#5b52f9] dark:text-indigo-400 border border-indigo-200/60 dark:border-indigo-800',
    mark: 'bg-[#5b52f9]',
    linkHover: 'group-hover:text-[#5b52f9]',
  },
  success: {
    label: 'text-slate-600 dark:text-slate-300',
    value: 'text-slate-900 dark:text-white',
    cardBg: 'bg-gradient-to-b from-white to-[#f0fdf4] dark:from-slate-900 dark:to-emerald-950/20 border-emerald-100/90 dark:border-emerald-900/50 shadow-[0_4px_14px_rgba(16,185,129,0.06)]',
    iconBg: 'bg-emerald-100/80 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 border border-emerald-200/60 dark:border-emerald-800',
    mark: 'bg-emerald-500',
    linkHover: 'group-hover:text-emerald-600',
  },
  warning: {
    label: 'text-slate-600 dark:text-slate-300',
    value: 'text-slate-900 dark:text-white',
    cardBg: 'bg-gradient-to-b from-white to-[#fffbeb] dark:from-slate-900 dark:to-amber-950/20 border-amber-100/90 dark:border-amber-900/50 shadow-[0_4px_14px_rgba(245,158,11,0.06)]',
    iconBg: 'bg-amber-100/80 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 border border-amber-200/60 dark:border-amber-800',
    mark: 'bg-amber-500',
    linkHover: 'group-hover:text-amber-600',
  },
  critical: {
    label: 'text-slate-600 dark:text-slate-300',
    value: 'text-slate-900 dark:text-white',
    cardBg: 'bg-gradient-to-b from-white to-[#fff1f2] dark:from-slate-900 dark:to-rose-950/20 border-rose-100/90 dark:border-rose-900/50 shadow-[0_4px_14px_rgba(244,63,94,0.06)]',
    iconBg: 'bg-rose-100/80 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 border border-rose-200/60 dark:border-rose-800',
    mark: 'bg-rose-500',
    linkHover: 'group-hover:text-rose-600',
  },
} as const satisfies Record<TileTone, Record<string, string>>;

export function DeckStatTile({
  label,
  value,
  hint,
  icon,
  tone = 'default',
  share,
  shareLabel,
  isLoading = false,
  to,
  linkLabel,
}: DeckStatTileProps) {
  const isRevealed = useReveal();
  const palette = TONE[tone];
  const drawn = share === undefined ? null : Math.max(0, Math.min(1, share)) * 100;

  const content = (
    <div className="flex flex-col justify-between h-full">
      <div>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className={cn('text-[14px] font-semibold tracking-normal', palette.label)}>{label}</p>
            {isLoading ? (
              <Skeleton className="mt-1.5 h-8 w-16" />
            ) : (
              <p className={cn('mt-1 text-[28px] font-black tracking-tight leading-none', palette.value)}>
                <CountUp value={value} />
              </p>
            )}
          </div>
          <span
            className={cn(
              'grid h-9.5 w-9.5 shrink-0 place-items-center rounded-xl transition-transform duration-300 group-hover:scale-105 shadow-2xs',
              palette.iconBg,
            )}
            aria-hidden
          >
            {icon}
          </span>
        </div>

        {hint && <p className="mt-1.5 text-[11.5px] text-slate-400 dark:text-slate-500 font-medium">{hint}</p>}
      </div>

      <div>
        {/* Subtle, small progress bar */}
        {drawn !== null && drawn > 0 && !isLoading && (
          <div
            className="mt-2.5 h-1 w-full overflow-hidden rounded-full bg-slate-100/80 dark:bg-slate-800"
            role="img"
            aria-label={shareLabel ?? `${Math.round(drawn)} per cent of the total`}
          >
            <div
              className={cn(
                'h-full rounded-full transition-all duration-1000 ease-out',
                palette.mark,
              )}
              style={{ width: isRevealed ? `${drawn}%` : '0%' }}
            />
          </div>
        )}

        {to && (
          <div className="mt-2.5 flex items-center gap-1.5 text-[12px] font-semibold text-slate-400 dark:text-slate-500 transition-colors group-hover:text-[#5b52f9]">
            <span>{linkLabel ?? 'See institutes'}</span>
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
          </div>
        )}
      </div>
    </div>
  );

  const containerClass = cn(
    'group relative flex flex-col justify-between rounded-2xl border p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md',
    palette.cardBg,
  );

  if (!to) {
    return <div className={containerClass}>{content}</div>;
  }

  return (
    <Link to={to} className={cn(containerClass, 'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5b52f9]')}>
      {content}
    </Link>
  );
}
